import { useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import { normalizarLlaveRepuesto } from '@actual-app/core/shared/llave-repuesto';
import type { TFunction } from 'i18next';

import { Boton } from '#components/mobile/ui/Boton';
import { Icono } from '#components/mobile/ui/Icono';
import { color, radio, texto } from '#components/mobile/ui/tokens';
import { useDispatch } from '#redux';
import { signOut } from '#users/usersSlice';

import { CampoContrasena, estiloCampo } from './CampoContrasena';
import { LlaveRepuesto } from './LlaveRepuesto';
import { NuevaContrasena } from './NuevaContrasena';

export type ModoCofre =
  | 'crear'
  | 'llave'
  | 'abrir'
  | 'repuesto'
  | 'nueva'
  | 'cero';

type PantallaCofreProps = {
  /** «sin-llavero» empieza creando la contraseña; «cerrado», abriendo. */
  estado: 'sin-llavero' | 'cerrado';
  onListo: () => void;
};

export function mensajeErrorCofre(t: TFunction, error: string): string {
  switch (error) {
    case 'contrasena-incorrecta':
      return t("That isn't the password. Try again.");
    case 'repuesto-incorrecto':
      return t("That spare key doesn't open this vault. Check it.");
    case 'contrasena-no-valida':
      return t(
        'The password needs 8 characters or more, with letters and numbers.',
      );
    case 'llavero-existe':
      return t(
        'You already created your Rumbo password on another device. Open the vault with it.',
      );
    default:
      return t(
        "We couldn't reach the server. Check your connection and try again.",
      );
  }
}

/**
 * El cofre de la persona en este aparato: crear la contraseña de Rumbo (con
 * su llave de repuesto) o abrirlo con ella. Se pide una vez por aparato.
 */
export function PantallaCofre({ estado, onListo }: PantallaCofreProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [modo, setModo] = useState<ModoCofre>(
    estado === 'sin-llavero' ? 'crear' : 'abrir',
  );
  const [llave, setLlave] = useState('');

  async function crear(contrasena: string, deCero: boolean) {
    const res = await send(deCero ? 'cofre-empezar-de-cero' : 'cofre-crear', {
      contrasena,
    });
    if (res.error || !res.llaveRepuesto) {
      if (res.error === 'llavero-existe') {
        setModo('abrir');
      }
      return mensajeErrorCofre(t, res.error ?? 'sin-conexion');
    }
    setLlave(res.llaveRepuesto);
    setModo('llave');
    return null;
  }

  async function cambiar(nueva: string) {
    const res = await send('cofre-cambiar-contrasena', { nueva });
    if (res.error) {
      return mensajeErrorCofre(t, res.error);
    }
    onListo();
    return null;
  }

  let contenido: ReactNode;
  switch (modo) {
    case 'crear':
      contenido = (
        <Marco
          titulo={t('Create your Rumbo password')}
          texto={t(
            'Your data travels in a locked vault. Only this password opens it: neither Google nor Rumbo can.',
          )}
        >
          <NuevaContrasena
            textoBoton={t('Create password')}
            onEnviar={c => crear(c, false)}
          />
        </Marco>
      );
      break;
    case 'cero':
      contenido = (
        <Marco
          titulo={t('Start from scratch')}
          texto={t(
            "You'll get a new vault. What was locked with the old one can't be opened, unless someone you share a budget with opens it for you again or you have a .zip copy.",
          )}
          aviso
          onVolver={() => setModo('abrir')}
        >
          <NuevaContrasena
            textoBoton={t('Start from scratch')}
            onEnviar={c => crear(c, true)}
          />
        </Marco>
      );
      break;
    case 'llave':
      contenido = (
        <Marco
          titulo={t('Save your spare key')}
          texto={t(
            'If you forget your password, this key opens your data. You will only see it now.',
          )}
        >
          <LlaveRepuesto llave={llave} onContinuar={onListo} />
        </Marco>
      );
      break;
    case 'repuesto':
      contenido = (
        <AbrirConRepuesto
          onVolver={() => setModo('abrir')}
          onAbierto={() => setModo('nueva')}
          onSinNada={() => setModo('cero')}
        />
      );
      break;
    case 'nueva':
      contenido = (
        <Marco
          titulo={t('Choose a new password')}
          texto={t(
            'The vault is open. Choose a new Rumbo password so you can open it next time.',
          )}
        >
          <NuevaContrasena textoBoton={t('Save')} onEnviar={cambiar} />
        </Marco>
      );
      break;
    default:
      contenido = (
        <AbrirConContrasena
          onAbierto={onListo}
          onRepuesto={() => setModo('repuesto')}
        />
      );
  }

  return (
    <View
      style={{
        width: '100%',
        maxWidth: 420,
        maxHeight: '100%',
        overflowY: 'auto',
        padding: '24px 4px',
        gap: 24,
      }}
    >
      {contenido}
      {modo !== 'llave' && (
        <button
          type="button"
          onClick={() => void dispatch(signOut())}
          style={{
            ...texto.secundario,
            alignSelf: 'center',
            minHeight: 44,
            padding: '0 8px',
            background: 'transparent',
            border: 0,
            color: color.fg3,
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          <Trans>Sign out</Trans>
        </button>
      )}
    </View>
  );
}

export function Marco({
  titulo,
  texto: explicacion,
  aviso,
  onVolver,
  children,
}: {
  titulo: string;
  texto: string;
  aviso?: boolean;
  onVolver?: () => void;
  children?: ReactNode;
}) {
  return (
    <View style={{ gap: 20 }}>
      {onVolver && (
        <button
          type="button"
          onClick={onVolver}
          style={{
            ...texto.cuerpo,
            alignSelf: 'flex-start',
            minHeight: 44,
            padding: 0,
            background: 'transparent',
            border: 0,
            color: color.accent,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          <Trans>Back</Trans>
        </button>
      )}
      <View
        style={{
          width: 56,
          height: 56,
          borderRadius: 18,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: aviso ? color.warnSoft : color.accentSoft,
          color: aviso ? color.warn : color.accent,
        }}
      >
        <Icono nombre="lock" size={28} />
      </View>
      <View style={{ gap: 8 }}>
        <h1 style={{ ...texto.display, margin: 0, color: color.fg }}>
          {titulo}
        </h1>
        <Text style={{ ...texto.cuerpo, fontSize: 15, color: color.fg2 }}>
          {explicacion}
        </Text>
      </View>
      {children}
    </View>
  );
}

function AbrirConContrasena({
  onAbierto,
  onRepuesto,
}: {
  onAbierto: () => void;
  onRepuesto: () => void;
}) {
  const { t } = useTranslation();
  const [contrasena, setContrasena] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [abriendo, setAbriendo] = useState(false);

  async function abrir() {
    if (!contrasena || abriendo) {
      return;
    }
    setAbriendo(true);
    setError(null);
    const res = await send('cofre-abrir', { contrasena });
    setAbriendo(false);
    if (res.error) {
      setError(mensajeErrorCofre(t, res.error));
      return;
    }
    onAbierto();
  }

  return (
    <Marco
      titulo={t('Open your vault')}
      texto={t(
        "Type your Rumbo password to open your data on this device. We won't ask for it again here.",
      )}
    >
      <form
        onSubmit={e => {
          e.preventDefault();
          void abrir();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
      >
        <CampoContrasena
          etiqueta={t('Rumbo password')}
          valor={contrasena}
          onCambio={setContrasena}
          estado={error ? 'error' : undefined}
          autoComplete="current-password"
          autoFocus
        >
          {error && (
            <Text
              role="alert"
              style={{ ...texto.secundario, color: color.bad }}
            >
              {error}
            </Text>
          )}
        </CampoContrasena>
        <Boton bloque type="submit" isDisabled={!contrasena || abriendo}>
          <Trans>Open</Trans>
        </Boton>
        <Enlace onPress={onRepuesto}>{t("I don't remember it")}</Enlace>
      </form>
    </Marco>
  );
}

export function AbrirConRepuesto({
  onVolver,
  onAbierto,
  onSinNada,
  abrirCon = llaveRepuesto => send('cofre-abrir', { llaveRepuesto }),
}: {
  onVolver?: () => void;
  onAbierto: () => void;
  onSinNada: () => void;
  /** Por defecto abre el cofre de esta sesión; el alta abre el suyo. */
  abrirCon?: (llaveRepuesto: string) => Promise<{ error?: string }>;
}) {
  const { t } = useTranslation();
  const [escrita, setEscrita] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [abriendo, setAbriendo] = useState(false);
  const valida = normalizarLlaveRepuesto(escrita) !== null;

  async function pegar() {
    try {
      setEscrita(await navigator.clipboard.readText());
    } catch {
      setError(
        t("We couldn't paste it. Long-press the field and paste it there."),
      );
    }
  }

  async function abrir() {
    if (!valida || abriendo) {
      return;
    }
    setAbriendo(true);
    setError(null);
    const res = await abrirCon(escrita);
    setAbriendo(false);
    if (res.error) {
      setError(mensajeErrorCofre(t, res.error));
      return;
    }
    onAbierto();
  }

  return (
    <Marco
      titulo={t('Open it with your spare key')}
      texto={t(
        "Paste it or type it (capitals and spaces don't matter). It's the 24-character key you saved when you created the password.",
      )}
      onVolver={onVolver}
    >
      <form
        onSubmit={e => {
          e.preventDefault();
          void abrir();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <input
          aria-label={t('Spare key')}
          value={escrita}
          onChange={e => setEscrita(e.target.value)}
          placeholder="XXXX XXXX XXXX XXXX XXXX XXXX"
          autoCapitalize="characters"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          style={{
            ...estiloCampo(
              error ? color.bad : valida ? color.accent : color.line,
            ),
            padding: '0 14px',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontWeight: 700,
            letterSpacing: '0.04em',
          }}
        />
        <Boton variante="fantasma" onPress={() => void pegar()}>
          <Icono nombre="copy" size={18} />
          <Trans>Paste</Trans>
        </Boton>
        {error && (
          <Text role="alert" style={{ ...texto.secundario, color: color.bad }}>
            {error}
          </Text>
        )}
        <Boton bloque type="submit" isDisabled={!valida || abriendo}>
          <Trans>Open the vault</Trans>
        </Boton>
      </form>
      <View
        style={{
          padding: '14px 16px',
          border: `1px solid ${color.line}`,
          borderRadius: radio.boton,
          gap: 6,
        }}
      >
        <Text style={{ ...texto.secundario, color: color.fg2 }}>
          {t(
            'No key and no password? Nobody, not even Rumbo, can recover that data. If you share a budget with someone, they can open it for you again from their device.',
          )}
        </Text>
        <Enlace onPress={onSinNada} peligro>
          <Trans>Start from scratch</Trans>
        </Enlace>
      </View>
    </Marco>
  );
}

export function Enlace({
  onPress,
  peligro,
  children,
}: {
  onPress: () => void;
  peligro?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onPress}
      style={{
        ...texto.cuerpo,
        alignSelf: 'flex-start',
        minHeight: 44,
        padding: 0,
        background: 'transparent',
        border: 0,
        color: peligro ? color.bad : color.accent,
        fontWeight: 700,
        textDecoration: peligro ? 'underline' : undefined,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}
